export interface IUserDetailsState {
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  id: number;
  fbId: string;
  parentId: number;
  parentFbId: string;
  username: string;
  email: string;
  role: string;
  roleName: string;
  countryId: number;
  status: boolean;
  barcodeScanEnabled: boolean;
  phoneCode: string;
  phoneNumber: string;
  quota: {
    coupon: {
      renewDate: number;
      isCancellation: boolean;
      dailyCancellation: number;
    };
  };
  customerNo: string;
  dateOfBirth: string;
  street: string;
  postCode: string;
  location: string;
  countryCurrency: string;
  countryName: string;
  firstname: string;
  lastname: string;
  country: ICountry;
  balance: number;
  availableWithdrawalAmount: number;
  /**
   * The SERVER's per-account verdict on the virtuals product, shipped as an
   * additive field on GET /user/getUserAccount (design D1/D2). Optional on
   * the wire because an older backend does not send it; never optional in the
   * store, where the reducer always writes a normalised value (see
   * normaliseVirtualsVerdict) so a missing or malformed field reads as
   * HIDDEN rather than as whatever the previous payload said.
   */
  virtuals?: IVirtualsVerdict;
}

/**
 * GET /user/getUserAccount `virtuals`. `visible` is the only field that
 * decides anything; `reason` (first failing backend rule, e.g.
 * PROVIDER_NOT_ENABLED_FOR_USER) is informational, and `checkedAt` is the
 * server's epoch ms, which drives the client's refresh TTL
 * (shared/services/virtuals-verdict.ts).
 */
export interface IVirtualsVerdict {
  visible: boolean;
  reason: string | null;
  checkedAt: number;
}

/**
 * What every virtuals consumer reads (selectVirtualsVerdict): the verdict
 * together with the account it was computed for. The fbId is half the
 * answer — a verdict is only ever applied to the signed-in credential whose
 * uid it matches, so a store still holding the PREVIOUS account (logout used
 * to leave it there, and a no-reload login briefly still does) can never
 * open the entry for the next one.
 */
export interface IAccountVirtualsVerdict extends IVirtualsVerdict {
  fbId: string;
}

export interface ICountry {
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  id: number;
  currency: string;
  isDefault: boolean;
  maxWinnings: number;
  minStakes: number;
  name: string;
  stakes: number[];
  stakesMultiplier: number;
}

export const initialUserDetailsState: IUserDetailsState = {
  createdAt: '',
  updatedAt: '',
  deletedAt: null,
  id: 0,
  fbId: '',
  parentId: 0,
  parentFbId: '',
  username: '',
  email: '',
  role: '',
  roleName: '',
  countryId: 0,
  status: false,
  barcodeScanEnabled: false,
  phoneCode: '',
  phoneNumber: '',
  quota: {
    coupon: {
      renewDate: 0,
      isCancellation: false,
      dailyCancellation: 0,
    },
  },
  customerNo: '',
  dateOfBirth: '',
  street: '',
  postCode: '',
  location: '',
  countryCurrency: '',
  countryName: '',
  firstname: '',
  lastname: '',
  country: {
    deletedAt: null,
    id: 0,
    currency: '',
    isDefault: false,
    createdAt: '',
    updatedAt: '',
    maxWinnings: 0,
    minStakes: 0,
    name: '',
    stakes: [],
    stakesMultiplier: 0,
  },
  balance: 0,
  availableWithdrawalAmount: 0,
  // Hidden until the server says otherwise. Mandatory, not cosmetic:
  // clearUserDetailsData merges THIS object over the current state, so a
  // field missing here would survive a logout.
  virtuals: {
    visible: false,
    reason: null,
    checkedAt: 0,
  },
};
